/**
 * The single source of TanStack Query keys. Centralizing them keeps cache
 * invalidation correct and refactor-safe. Each feature adds its namespace here;
 * never inline an array key in a hook.
 */
export const queryKeys = {
  me: {
    all: ["me"] as const,
    detail: () => ["me", "detail"] as const,
    preferences: () => ["me", "preferences"] as const,
    /** Prefix matching every paginated memory page — for invalidation. */
    memoriesRoot: ["me", "memories"] as const,
    memories: (params: { page: number; perPage: number }) =>
      ["me", "memories", params] as const,
  },
  conversations: {
    all: ["conversations"] as const,
    list: (params: { search?: string } = {}) =>
      ["conversations", "list", params] as const,
    detail: (id: string) => ["conversations", "detail", id] as const,
  },
  sharing: {
    all: ["sharing"] as const,
    /** Public, read-only shared conversation, keyed by its share token. */
    detail: (token: string) => ["sharing", "detail", token] as const,
  },
  billing: {
    all: ["billing"] as const,
    usage: () => ["billing", "usage"] as const,
    subscription: () => ["billing", "subscription"] as const,
  },
  chat: {
    all: ["chat"] as const,
    models: () => ["chat", "models"] as const,
    modelUsage: () => ["chat", "model-usage"] as const,
    /** The assistant a new chat is scoped to (welcome hero + default model). */
    assistant: (id: string) => ["chat", "assistant", id] as const,
    /** Generated conversation starters for that assistant. */
    assistantStarters: (id: string) => ["chat", "assistant", id, "starters"] as const,
    /** Async image-generation job spawned by the in-chat `generate_image` tool. */
    imageJob: (id: string) => ["chat", "image-job", id] as const,
    /** Async video-generation job spawned by the in-chat `generate_video` tool. */
    videoJob: (id: string) => ["chat", "video-job", id] as const,
  },
  images: {
    all: ["images"] as const,
    config: () => ["images", "config"] as const,
    /** Prefix matching every paginated set list (any filter) — for invalidation. */
    setsRoot: ["images", "sets"] as const,
    sets: (params: { favorite?: boolean; search?: string } = {}) =>
      ["images", "sets", params] as const,
  },
  videos: {
    all: ["videos"] as const,
    config: () => ["videos", "config"] as const,
    /** Prefix matching every set-list variant — for invalidation. */
    setsRoot: ["videos", "sets"] as const,
    sets: (params: { favorite?: boolean; search?: string } = {}) =>
      ["videos", "sets", params] as const,
  },
  library: {
    all: ["library"] as const,
    /** Prefix matching every paginated library page (any filter) — for invalidation. */
    listRoot: ["library", "list"] as const,
    list: (params: { type?: string } = {}) => ["library", "list", params] as const,
  },
  connectors: {
    all: ["connectors"] as const,
    catalog: () => ["connectors", "catalog"] as const,
    /** The user's connected MCP servers — shared by settings and the composer picker. */
    mine: () => ["connectors", "mine"] as const,
  },
  assistants: {
    all: ["assistants"] as const,
    /** Prefix matching every public-list variant (any filter) — for invalidation. */
    listRoot: ["assistants", "list"] as const,
    list: (
      params: { search?: string; sort?: string; category?: string; limit?: number } = {},
    ) => ["assistants", "list", params] as const,
    categories: () => ["assistants", "categories"] as const,
    detail: (id: string) => ["assistants", "detail", id] as const,
    mine: () => ["assistants", "mine"] as const,
    pinned: () => ["assistants", "pinned"] as const,
    myRating: (id: string) => ["assistants", "rating", id] as const,
  },
  skills: {
    all: ["skills"] as const,
    /** The user's own personal skills — shared by the manager and the composer slash flow. */
    mine: () => ["skills", "mine"] as const,
    /** A skill's reference-file tree (path + size). */
    files: (id: string) => ["skills", id, "files"] as const,
    /** One reference file's content, keyed by skill id + exact path. */
    fileContent: (id: string, path: string) => ["skills", id, "files", path] as const,
  },
  ugc: {
    all: ["ugc"] as const,
    campaignsRoot: ["ugc", "campaigns"] as const,
    campaigns: (status?: string) => ["ugc", "campaigns", "list", { status }] as const,
    campaign: (id: string) => ["ugc", "campaigns", "detail", id] as const,
    identitiesRoot: ["ugc", "identities"] as const,
    identities: () => ["ugc", "identities"] as const,
    identity: (id: string) => ["ugc", "identities", "detail", id] as const,
    brandKitsRoot: ["ugc", "brand-kits"] as const,
    brandKits: () => ["ugc", "brand-kits"] as const,
    brandKit: (id: string) => ["ugc", "brand-kits", "detail", id] as const,
    productKitsRoot: ["ugc", "product-kits"] as const,
    productKits: () => ["ugc", "product-kits"] as const,
    productKit: (id: string) => ["ugc", "product-kits", "detail", id] as const,
    jobsRoot: ["ugc", "jobs"] as const,
    jobs: (params: { campaignId?: string; kind?: string; status?: string } = {}) =>
      ["ugc", "jobs", "list", params] as const,
    job: (id: string) => ["ugc", "jobs", "detail", id] as const,
    jobEvents: (id: string) => ["ugc", "jobs", "detail", id, "events"] as const,
    scenes: (campaignId: string) => ["ugc", "campaigns", "detail", campaignId, "scenes"] as const,
  },
} as const;
